import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminBookings } from '../services/bookingsApi';
import { formatDateOnly, formatDateRange } from '../utils/dateFormat';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';
const STATUS_ORDER = [
  'pending',
  'awaiting_payment',
  'confirmed',
  'checked_in',
  'checked_out',
  'completed',
  'cancel_requested',
  'cancelled',
  'declined',
];
const PAYMENT_ORDER = ['unpaid', 'paid', 'partial', 'refunded', 'failed'];

const formatStatusLabel = (value) => String(value || 'unknown').replace(/_/g, ' ');
const formatAmount = (currency, amount) => {
  const value = Number(amount || 0);
  return `${currency || 'INR'} ${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
};
const formatPaymentMode = (value) => (String(value || 'full').replace(/_/g, ' '));

const AdminStatusChip = ({ value }) => (
  <span className="px-2 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-800 capitalize">
    {formatStatusLabel(value)}
  </span>
);

const BookingsPage = ({ token, view = 'all' }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState({ status: '', payment_status: '', q: '', date_from: '', date_to: '' });
  const [pagination, setPagination] = useState({ page: 1, totalPages: 1 });
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const navigate = useNavigate();

  const load = async (page = 1) => {
    setLoading(true);
    setError('');
    try {
      const res = await adminBookings.list(token, { ...filters, view, page, limit: 20 });
      setItems(res.data || []);
      setPagination(res.pagination || { page: 1, totalPages: 1 });
    } catch (err) {
      setError(err.message || 'Failed to load bookings');
    } finally {
      setLoading(false);
    }
  };

  const loadSummary = async () => {
    setSummaryLoading(true);
    try {
      const res = await adminBookings.summary(token, { ...filters, view });
      setSummary(res);
    } catch {
      setSummary(null);
    } finally {
      setSummaryLoading(false);
    }
  };

  const buildQuery = (overrides = {}) => {
    const params = new URLSearchParams();
    const payload = { ...filters, view, ...overrides };
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        params.set(key, value);
      }
    });
    return params.toString();
  };

  const exportCsv = async () => {
    if (!token) {
      setError('Missing admin token');
      return;
    }
    setExporting(true);
    setError('');
    try {
      const query = buildQuery({ limit: 20000 });
      const res = await fetch(`${API_BASE}/api/admin/bookings/export?${query}`, {
        headers: { Authorization: `Bearer ${token}` },
        credentials: 'include',
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(text || 'Export failed');
      }
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `bookings-export-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.message || 'Export failed');
    } finally {
      setExporting(false);
    }
  };

  const escapeHtml = (value) =>
    String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');

  const printTable = () => {
    if (!items.length) return;
    const win = window.open('', 'print', 'width=1000,height=700');
    if (!win) return;
    const rows = items
      .map(
        (b) => `<tr>
          <td>${escapeHtml(b.booking_code)}</td>
          <td>${escapeHtml(b.property?.title || '-')}</td>
          <td>${escapeHtml(b.host?.full_name || b.host?.business_name || '-')}</td>
          <td>${escapeHtml(b.guest?.name || b.customer_name || '-')}</td>
          <td>${escapeHtml(formatDateOnly(b.created_at || b.createdAt))}</td>
          <td>${escapeHtml(formatDateOnly(b.check_in_date))}</td>
          <td>${escapeHtml(formatDateOnly(b.check_out_date))}</td>
          <td>${escapeHtml(formatAmount(b.currency, b.total_amount))}</td>
          <td>${escapeHtml(b.payment_status || '-')}</td>
          <td>${escapeHtml(b.booking_status || '-')}</td>
        </tr>`
      )
      .join('');
    win.document.write(`<!doctype html><html><head><title>Bookings</title>
      <style>
        body { font-family: Arial, sans-serif; padding: 16px; }
        h1 { font-size: 18px; margin-bottom: 12px; }
        table { width: 100%; border-collapse: collapse; font-size: 12px; }
        th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
        th { background: #f3f4f6; }
      </style>
      </head><body>
      <h1 className="admin-page-title">Bookings</h1>
      <table>
        <thead>
          <tr>
            <th>Code</th>
            <th>Property</th>
            <th>Host</th>
            <th>Guest</th>
            <th>Booking Date</th>
            <th>Check-in</th>
            <th>Check-out</th>
            <th>Total</th>
            <th>Payment</th>
            <th>Status</th>
          </tr>
        </thead>
        <tbody>${rows}</tbody>
      </table>
      </body></html>`);
    win.document.close();
    win.focus();
    win.print();
    win.close();
  };

  useEffect(() => {
    load(1);
    loadSummary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.payment_status, filters.q, filters.date_from, filters.date_to, view]);

  return (
    <div className="p-4 bg-white rounded shadow-sm space-y-3">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
        <div>
          <h4 className="mb-0">Bookings</h4>
          <small className="text-muted">Platform-wide visibility</small>
        </div>
        <div className="d-flex flex-wrap gap-2">
          <button type="button" className="btn btn-sm btn-outline-primary" onClick={() => navigate('/admin/reports')}>
            Reports
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={printTable}
            disabled={!items.length}
          >
            Print
          </button>
          <button
            type="button"
            className="btn btn-sm btn-outline-secondary"
            onClick={exportCsv}
            disabled={exporting}
          >
            {exporting ? 'Exporting...' : 'Export CSV'}
          </button>
          <button type="button" className="btn btn-sm btn-outline-secondary" onClick={() => load(pagination.page)}>
            Refresh
          </button>
        </div>
      </div>

      <div className="d-flex gap-2 flex-wrap">
        <input
          className="form-control w-auto"
          placeholder="Search code/guest"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
        />
        <input
          type="date"
          className="form-control w-auto"
          value={filters.date_from}
          onChange={(e) => setFilters((f) => ({ ...f, date_from: e.target.value }))}
        />
        <input
          type="date"
          className="form-control w-auto"
          value={filters.date_to}
          onChange={(e) => setFilters((f) => ({ ...f, date_to: e.target.value }))}
        />
        <select
          className="form-select w-auto"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
        >
          <option value="">Status</option>
          {STATUS_ORDER.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>
        <select
          className="form-select w-auto"
          value={filters.payment_status}
          onChange={(e) => setFilters((f) => ({ ...f, payment_status: e.target.value }))}
        >
          <option value="">Payment</option>
          {PAYMENT_ORDER.map((p) => (
            <option key={p}>{p}</option>
          ))}
        </select>
      </div>

      {summaryLoading && (
        <div className="text-muted small">Loading booking summary...</div>
      )}
      {summary && (
        <div className="border rounded p-3">
          <div className="d-flex flex-wrap gap-4">
            <div>
              <div className="small text-muted">Total bookings</div>
              <div className="fw-semibold">{summary.totalBookings || 0}</div>
            </div>
            <div>
              <div className="small text-muted">Total amount</div>
              <div className="fw-semibold">{formatAmount(summary.currency, summary.totalAmount)}</div>
            </div>
            <div>
              <div className="small text-muted">Paid amount</div>
              <div className="fw-semibold">{formatAmount(summary.currency, summary.amountPaid || 0)}</div>
            </div>
            <div>
              <div className="small text-muted">Pending amount</div>
              <div className="fw-semibold">{formatAmount(summary.currency, summary.pendingAmount || 0)}</div>
            </div>
            <div className="flex-grow-1">
              <div className="small text-muted">Status</div>
              <div className="d-flex flex-wrap gap-1">
                {STATUS_ORDER.filter((status) => summary.statuses?.[status]).map((status) => (
                  <span key={status} className="badge bg-light text-dark">
                    {formatStatusLabel(status)}: {summary.statuses[status]}
                  </span>
                ))}
              </div>
            </div>
            <div className="flex-grow-1">
              <div className="small text-muted">Payments</div>
              <div className="d-flex flex-wrap gap-1">
                {PAYMENT_ORDER.filter((status) => summary.payments?.[status]).map((status) => (
                  <span key={status} className="badge bg-light text-dark">
                    {formatStatusLabel(status)}: {summary.payments[status]}
                  </span>
                ))}
              </div>
            </div>
            {!!Object.keys(summary.paymentModes || {}).length && (
              <div className="flex-grow-1">
                <div className="small text-muted">Payment mode</div>
                <div className="d-flex flex-wrap gap-1">
                  {Object.entries(summary.paymentModes || {}).map(([mode, count]) => (
                    <span key={mode} className="badge bg-light text-dark">
                      {formatPaymentMode(mode)}: {count}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {error && <div className="alert alert-danger">{error}</div>}
      {loading && (
        <div className="text-center text-muted">
          <div className="spinner-border spinner-border-sm" role="status" /> Loading...
        </div>
      )}

      {!loading && (
        <div className="table-responsive">
          <table className="table table-hover align-middle">
            <thead className="table-light">
              <tr>
                <th>Code</th>
                <th>Property</th>
                <th>Host</th>
                <th>Guest</th>
                <th>Booking Date</th>
                <th>Dates</th>
                <th>Total</th>
                <th>Payment</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((b) => (
                <tr key={b.id} style={{ cursor: 'pointer' }} onClick={() => navigate(`/admin/bookings/${b.id}`)}>
                  <td className="fw-semibold">{b.booking_code}</td>
                  <td>{b.property?.title || '-'}</td>
                  <td>{b.host?.full_name || b.host?.business_name || '-'}</td>
                  <td>{b.guest?.name || b.customer_name || '-'}</td>
                  <td>{formatDateOnly(b.created_at || b.createdAt)}</td>
                  <td>
                    {formatDateRange(b.check_in_date, b.check_out_date)}
                  </td>
                  <td>
                    {formatAmount(b.currency, b.total_amount)}
                    <div className="small text-muted">
                      Paid {formatAmount(b.currency, b.amount_paid_now ?? b.total_amount ?? 0)} | Pending {formatAmount(b.currency, b.remaining_amount ?? 0)}
                    </div>
                  </td>
                  <td>
                    <AdminStatusChip value={b.payment_status} />
                    <div className="small text-muted mt-1">{formatPaymentMode(b.payment_option_selected)}</div>
                  </td>
                  <td>
                    <AdminStatusChip value={b.booking_status} />
                  </td>
                </tr>
              ))}
              {items.length === 0 && (
                <tr>
                  <td colSpan={9} className="text-center text-muted py-4">
                    No bookings found for the current filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      <div className="d-flex justify-content-between align-items-center">
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={loading || pagination.page <= 1}
          onClick={() => load(Math.max(1, pagination.page - 1))}
        >
          Previous
        </button>
        <span className="text-muted">
          Page {pagination.page} of {pagination.totalPages}
        </span>
        <button
          type="button"
          className="btn btn-sm btn-outline-secondary"
          disabled={loading || pagination.page >= pagination.totalPages}
          onClick={() => load(Math.min(pagination.totalPages, pagination.page + 1))}
        >
          Next
        </button>
      </div>
    </div>
  );
};

export default BookingsPage;



